/**
 * fileToResizedDataUrl — read an image File, downscale it to fit within
 * `maxDim` px on its longest side, and return a compressed JPEG base64 data
 * URL. Keeps uploads small and comfortably within the backend's ~2 MB cap
 * (important for large phone photos) while staying crisp enough for a tag.
 */
export async function fileToResizedDataUrl(file, { maxDim = 1024, quality = 0.85, mime = 'image/jpeg' } = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Please choose an image file');
  }
  const srcDataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });

  const image = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('Could not load the image'));
    im.src = srcDataUrl;
  });

  const longest = Math.max(image.width, image.height) || 1;
  const scale = Math.min(1, maxDim / longest);
  const w = Math.max(1, Math.round(image.width * scale));
  const h = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // White backdrop so transparent PNGs don't come out black as JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(image, 0, 0, w, h);

  return canvas.toDataURL(mime, quality);
}

// Compress an image File until its base64 payload fits within `capKb`.
export async function fileToCappedDataUrl(file, capKb) {
  const capBytes = (Number(capKb) || 500) * 1024;
  const bytes = (d) => Math.floor((d.length - d.indexOf(',') - 1) * 3 / 4);
  let out = '';
  for (const maxDim of [1600, 1200, 1000, 800, 600, 400, 300]) {
    for (const quality of [0.85, 0.7, 0.6, 0.5, 0.4]) {
      out = await fileToResizedDataUrl(file, { maxDim, quality });
      if (bytes(out) <= capBytes) return out;
    }
  }
  return out;
}
